import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

import { Selo } from 'components/icons';
import { UserAvatar } from 'components/user-avatar';

import { barrasDeNivel, descreverSelo, ItemDeSelo, progressoDoCiclo, rotuloNivel, rotuloSelos } from '../perfil-calculos';

export interface PessoaDoPerfil {
  nome?: string;
  matricula?: string;
  cargo?: string;
  grupo?: string;
  /** Dados que o `UserAvatar` já sabe desenhar: `avatar_type`, `avatar`, `image`. */
  avatar: { avatar_type?: number; avatar?: unknown; image?: unknown };
  /** Verdadeiro enquanto o userInfo (cargo, grupo) não chegou. */
  carregando?: boolean;
}

interface PerfilHeroProps {
  pessoa: PessoaDoPerfil;
  nivel?: string;
  /** Falso para a Rede Joga Junto e para grupo não avaliável (PERFIL-10). */
  mostrarNivel: boolean;
  carregandoNivel: boolean;
  /** Do `get_selos`: só o mais recente, para o selo sobre a foto. */
  selos: ItemDeSelo[];
  /** Total da coleção de todos os ciclos; ausente quando a coleção falhou (AJH-13). */
  totalDeSelos?: number;
  carregandoSelos: boolean;
  ciclo?: { inicio?: number; fim?: number };
  onEditarFoto: () => void;
  /** Abre o detalhe do grupo (2S2023 e 1S2024, PERFIL-49); sem ele o grupo é texto. */
  onVerGrupo?: () => void;
  /** Instante de referência da barra do ciclo; injetável para teste. */
  agora?: number;
}

/** Anel da foto, igual ao do ProfileCard da Home. */
const ANEL_DA_FOTO =
  'bg-[conic-gradient(from_90deg,var(--brand-green-light),var(--brand-purple-light),var(--brand-pink-light),var(--brand-yellow-light),var(--brand-green-light))]';

const PILULA = 'rounded-jj-pill px-3.5 py-1.5 font-display text-[13px] font-bold';

function Carregando({ rotulo, className }: { rotulo: string; className: string }) {
  return <span role="status" aria-label={rotulo} className={`block animate-pulse rounded-jj-pill bg-white/20 ${className}`} />;
}

function formatarData(data?: number) {
  return data ? format(new Date(data), 'dd/MM/yyyy') : '';
}

export default function PerfilHero({
  pessoa,
  nivel,
  mostrarNivel,
  carregandoNivel,
  selos,
  totalDeSelos,
  carregandoSelos,
  ciclo,
  onEditarFoto,
  onVerGrupo,
  agora = Date.now(),
}: PerfilHeroProps) {
  const seloEmDestaque = selos[0] ? descreverSelo(selos[0]) : undefined;
  const progresso =
    ciclo?.inicio && ciclo?.fim ? Math.round(progressoDoCiclo(ciclo.inicio, ciclo.fim, agora)) : undefined;

  return (
    <section
      aria-label="Seu perfil"
      className="flex flex-col gap-5 rounded-2xl bg-brand-blue px-5 py-6 text-brand-white lg:px-8 lg:py-[26px]"
    >
      <div className="flex flex-wrap items-center gap-7">
        <div className="flex shrink-0 flex-col items-center gap-[11px] rounded-2xl bg-white/[0.13] p-3 shadow-[inset_0_0_0_1px_rgba(253,254,255,0.35)]">
          {/* Mesmo tratamento da foto na Home: anel em degradê das cores claras da marca
              (menta, lilás, rosa, creme) e o selo mais recente no canto inferior direito. */}
          <div className={`relative h-28 w-28 shrink-0 rounded-full p-1 ${ANEL_DA_FOTO}`}>
            <button
              type="button"
              onClick={onEditarFoto}
              aria-label="Editar avatar"
              className="block h-full w-full rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-white"
            >
              <UserAvatar data={{ ...pessoa.avatar, ariaHidden: true }} responsive />
            </button>
            {/* z-20: o avatar sem foto do UserAvatar tem z-10 e cobriria o selo. */}
            {seloEmDestaque && (
              <Link
                to="/selos"
                aria-label={`Ver meus selos. Selo mais recente: ${seloEmDestaque.nome} · ${seloEmDestaque.categoria}${
                  seloEmDestaque.ciclo ? `, ciclo ${seloEmDestaque.ciclo}` : ''
                }`}
                className="absolute bottom-2 right-[7px] z-20 drop-shadow-[0_2px_7px_rgba(0,45,75,0.28)]"
              >
                <span aria-hidden="true">
                  <Selo selo={seloEmDestaque.selo} cycle={seloEmDestaque.ciclo} size="32px" />
                </span>
              </Link>
            )}
          </div>
          <button
            type="button"
            onClick={onEditarFoto}
            aria-label="Editar foto do perfil"
            className="flex cursor-pointer items-center gap-[7px] rounded-jj-pill border-0 bg-brand-yellow px-[15px] py-2 font-display text-[11px] font-bold uppercase tracking-[0.04em] text-brand-blue transition hover:brightness-[0.94]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
              <path d="M20 3a1 1 0 0 1 1 1v1.757l-2 2V5H5v8.1l4-4 4.328 4.329-1.327 1.327-.006 4.239 4.246.006 1.33-1.33L18.9 19H19v-2.758l2-2V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h16Zm1.778 4.808 1.414 1.414L15.414 17l-1.416-.002.002-1.412 7.778-7.778ZM15.5 7a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" />
            </svg>
            Editar foto
          </button>
        </div>

        <div className="min-w-[240px] flex-1">
          <p className="m-0 font-display text-[26px] font-extrabold leading-[1.05] text-brand-yellow lg:text-[30px]">
            {pessoa.nome}
          </p>
          <p className="m-0 mt-2 flex flex-wrap items-center gap-2.5 text-sm text-white/90">
            <span>
              Matrícula: <b className="font-bold text-brand-cyan-light">{pessoa.matricula}</b>
            </span>
            {pessoa.carregando ? (
              <Carregando rotulo="Carregando cargo e grupo" className="h-4 w-56" />
            ) : (
              [pessoa.cargo, pessoa.grupo].filter(Boolean).map(dado => (
                <Fragment key={dado}>
                  <span aria-hidden="true" className="opacity-50">
                    /
                  </span>
                  {dado === pessoa.grupo && onVerGrupo ? (
                    <button
                      type="button"
                      onClick={onVerGrupo}
                      aria-label={`Ver detalhes do grupo ${dado}`}
                      className="cursor-pointer border-0 bg-transparent p-0 text-sm text-white/90 underline decoration-white/60 underline-offset-2 hover:text-brand-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-white"
                    >
                      {dado}
                    </button>
                  ) : (
                    <span>{dado}</span>
                  )}
                </Fragment>
              ))
            )}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2.5">
            {mostrarNivel &&
              (carregandoNivel ? (
                <Carregando rotulo="Carregando nível" className="h-[29px] w-40" />
              ) : (
                <span className={`${PILULA} bg-white/[0.16]`}>Nível: {rotuloNivel(nivel)}</span>
              ))}
            {carregandoSelos ? (
              <Carregando rotulo="Carregando selos" className="h-[29px] w-36" />
            ) : (
              totalDeSelos !== undefined && (
                <span className={`${PILULA} bg-brand-cyan-light text-content`}>{rotuloSelos(totalDeSelos)}</span>
              )
            )}
            {/* Link de texto, e não o botão amarelo, ao lado do total (AJH-11). */}
            <Link
              to="/selos"
              className="font-display text-[13px] font-bold text-brand-white underline underline-offset-2 hover:text-brand-white hover:no-underline"
            >
              Ver meus selos
            </Link>
          </div>
        </div>
      </div>

      {mostrarNivel && (
        <div className="flex flex-col gap-2.5 border-t border-white/[0.22] pt-[18px]">
          <p className="m-0 flex items-center gap-2 font-display text-[15px] font-bold">
            Acompanhe seu nível
            {!carregandoNivel && nivel && (
              <>
                <span aria-hidden="true" className="opacity-55">
                  /
                </span>
                <span className="text-brand-yellow">{rotuloNivel(nivel)}</span>
              </>
            )}
          </p>
          {carregandoNivel ? (
            <Carregando rotulo="Carregando barras de nível" className="h-[27px] w-full" />
          ) : (
            <ul className="m-0 flex list-none gap-1.5 p-0 lg:gap-2.5">
              {barrasDeNivel(nivel).map(barra => (
                <li key={barra.rotulo} className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className={`truncate text-[10px] tracking-[0.02em] lg:text-[11px] ${barra.preenchida ? 'text-brand-yellow' : 'text-white/75'}`}>
                    {barra.rotulo}
                  </span>
                  <div
                    role="progressbar"
                    aria-label={`Nível ${barra.rotulo}`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={barra.preenchida ? 100 : 0}
                    className="h-2.5 overflow-hidden rounded-jj-pill bg-white/25"
                  >
                    <div className="h-full rounded-jj-pill bg-brand-yellow" style={{ width: barra.preenchida ? '100%' : '0%' }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {progresso !== undefined && (
        <div className="flex flex-wrap items-center gap-3.5 border-t border-white/[0.22] pt-[18px]">
          <span className="whitespace-nowrap text-[13px]">
            De <b className="font-bold">{formatarData(ciclo?.inicio)}</b>
          </span>
          <div
            role="progressbar"
            aria-label="Andamento do ciclo"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progresso}
            className="h-2 min-w-[180px] flex-1 overflow-hidden rounded-jj-pill bg-white/25"
          >
            <div className="h-full rounded-jj-pill bg-brand-yellow" style={{ width: `${progresso}%` }} />
          </div>
          <span className="whitespace-nowrap text-[13px]">
            Ciclo encerra em <b className="text-brand-yellow">{formatarData(ciclo?.fim)}</b>
          </span>
        </div>
      )}
    </section>
  );
}
